import { FlyToInterpolator } from '@deck.gl/core/typed';
import { Map as MapIcon } from '@mui/icons-material';
import { IconButton, Tooltip } from '@mui/material';
import PreferencesMenu, { PreferencesMenuRefProps } from 'components/PreferencesMenu';
import { useViewState } from 'components/maps/BaseMap';
import { MapContext } from 'components/maps/MapContext';
import { useKineticMap } from 'components/maps/MapContext/hooks/useKineticMap';
import { generateId } from 'kineticUtils/functions';

import { memo, useCallback, useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { PREFERENCE_KEYS } from 'types/preferenceTypes';
import {
  LayerExtentPreferencesProps,
  LayerExtentUserPreference,
  LayerExtentUserPreferences,
} from './types';
import { useUserTenantPreference } from 'kineticUtils/hooks';

const LayerExtentPreferences: React.FunctionComponent<LayerExtentPreferencesProps> = ({
  className,
}) => {
  const { t } = useTranslation('maps');

  const { value: layoutPreferences, setPreference: savePreference } =
    useUserTenantPreference<LayerExtentUserPreferences>({ key: PREFERENCE_KEYS.EXTENT_LAYERS });

  const preferencesMenuRef = useRef<PreferencesMenuRefProps>(null);
  const map = useKineticMap();
  const viewState = useViewState();
  const { selectedLayers, setSelectedLayers } = useContext(MapContext);

  const handleSavePreference = (preferenceId?: string, newTitle?: string) => {
    let preferencesToSave = { ...layoutPreferences };
    if (!preferenceId) {
      const newPreference: LayerExtentUserPreference = {
        id: generateId(),
        name: newTitle ?? t('newMap'),
        viewState: viewState,
        default: false,
        layers: selectedLayers,
      };
      preferencesToSave = { ...preferencesToSave, [newPreference.id]: newPreference };
    } else {
      preferencesToSave[preferenceId] = {
        ...layoutPreferences[preferenceId],
        name: newTitle ?? layoutPreferences[preferenceId].name,
      };
    }
    savePreference(preferencesToSave);
  };

  const updatePreference = (preferenceId: string) => {
    let preferencesToSave = { ...layoutPreferences };
    preferencesToSave[preferenceId] = {
      ...layoutPreferences[preferenceId],
      viewState: viewState,
      layers: selectedLayers,
    };
    savePreference(preferencesToSave);
  };

  const toggleDefault = (preferenceId: string, isDefault?: boolean) => {
    let preferencesToSave = Object.entries(layoutPreferences).reduce((acc, [key, value]) => {
      const updatedPreference = { ...value };
      if (key === 'updatedAt') {
        acc[key] = value; // Keep the updatedAt value unchanged
      } else {
        updatedPreference.default = key === preferenceId ? isDefault : false;
        acc[key] = updatedPreference;
      }
      return acc;
    }, {} as LayerExtentUserPreferences);

    savePreference(preferencesToSave);
  };

  const handleDeletePreference = (preferenceId: string) => {
    const toSave = { ...layoutPreferences };
    delete toSave[preferenceId];
    savePreference(toSave);
  };

  const setPreference = useCallback(
    (preferenceId: string) => {
      const preference = layoutPreferences[preferenceId];
      if (preference.viewState) {
        map.setViewState({
          ...preference.viewState,
        });
      } else {
        // old preference was lat/lng so we need to reverse the bounds
        map.fitBounds(
          [
            [preference.bounds![0][1], preference.bounds![0][0]],
            [preference.bounds![1][1], preference.bounds![1][0]],
          ],
          0,
          0,
          {
            transitionDuration: 2000,
            transitionInterpolator: new FlyToInterpolator(),
          }
        );
      }
      setSelectedLayers(preference.layers ?? []);
    },
    [map, setSelectedLayers, layoutPreferences]
  );

  return (
    <>
      <Tooltip title={t('savedMaps')}>
        <IconButton
          className={className}
          onClick={(event) => preferencesMenuRef.current?.onOpenMenuButtonClick(event)}
          id='layerExtentPrefMenuButton'
        >
          <MapIcon fontSize='small' />
        </IconButton>
      </Tooltip>
      <PreferencesMenu
        ref={preferencesMenuRef}
        preferences={layoutPreferences}
        title={t('maps')}
        savePreference={handleSavePreference}
        setPreference={setPreference}
        deletePreference={handleDeletePreference}
        toggleDefault={toggleDefault}
        updatePreference={updatePreference}
      />
    </>
  );
};

export default memo(LayerExtentPreferences);
